import { createTokenUsingExchange } from 'app/api/exchange';
import { ProgramsAppState } from 'app/dux/programs';
import { SessionAppState } from 'app/dux/session';
import { getDelegatedOrCurrentCompanyId } from 'app/dux/session/selectors';
import { apiGetArcadeCabinets } from 'arcade/sections/cabinets/api';
import { apiGetArcadeRooms } from 'arcade/sections/rooms/api';
import { Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { apiGetArcades } from 'routes/arcades/api';
import { ArcadeListItem } from 'routes/arcades/interfaces';
import { RosterPersonMap } from 'shared/person-search-field/PersonSearchField';
import { ActionTypes, TokenBuilderAction } from './actions';
import { augmentRoom } from './augmentRoom';
import { buildExchangePayload } from './buildExchangePayload';
import { AugmentedRoom, TokenBuilderAppState } from './interfaces';
import { calculateValidationErrors, getArcade } from './selectors';

type RelevantAppState = SessionAppState & TokenBuilderAppState;
type TokenBuilderDispatch = Dispatch<TokenBuilderAction> & ThunkDispatch<RelevantAppState, any, TokenBuilderAction>;

export const setArcade = (arcade: ArcadeListItem) =>
    (dispatch: TokenBuilderDispatch, getState: () => RelevantAppState) => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);
        if (getArcade(state) === arcade) {
            return;
        }
        dispatch({ type: ActionTypes.SET_FORM_FIELDS, companyId, fields: { arcade } });
    };

export const loadArcades = () =>
    async (dispatch: TokenBuilderDispatch, getState: () => RelevantAppState) => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);

        const arcadeList = (await apiGetArcades()).arcades;

        // Load the rooms and cabinets for each arcade
        const roomList: AugmentedRoom[] = (
            await Promise.all(
                arcadeList.map(async (arcade): Promise<AugmentedRoom[]> => {
                    const [{ rooms }, { cabinets }] = await Promise.all([
                        apiGetArcadeRooms(arcade.id),
                        apiGetArcadeCabinets(arcade.id),
                    ]);

                    return rooms.map(room => augmentRoom(room, cabinets, arcade.id));
                })
            )
        ).flatMap(lists => lists);

        dispatch({ type: ActionTypes.SET_ARCADE_LIST, arcadeList, companyId, roomList });

        if (arcadeList.length > 0) {
            dispatch(setArcade(arcadeList[0]));
        }
    };

export const setCount = (count: number) =>
    (dispatch: TokenBuilderDispatch, getState: () => RelevantAppState) => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);
        dispatch({ type: ActionTypes.SET_FORM_FIELDS, companyId, fields: { count } });
    };

export const setRecipients = (
    recipientIds: string[],
    recipients: RosterPersonMap,
) =>
    (dispatch: TokenBuilderDispatch, getState: () => RelevantAppState) => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);

        dispatch({ type: ActionTypes.SET_FORM_FIELDS, companyId, fields: { recipientIds, recipients } });
    };

export const setRecognitionDistributionSelection = (
    recognitionDistributionId: string,
    value: boolean,
) =>
    (dispatch: TokenBuilderDispatch, getState: () => RelevantAppState) => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);

        dispatch({
            type: ActionTypes.SET_RECOGNITION_DISTRIBUTION_SELECTION,
            companyId,
            recognitionDistributionId,
            value,
        });
    };

export const createTokens = () =>
    async (
        dispatch: TokenBuilderDispatch,
        getState: () => RelevantAppState & ProgramsAppState
    ): Promise<{ createdTokenCount: number } | { errorMessage: string }> => {
        const state = getState();
        const companyId = getDelegatedOrCurrentCompanyId(state);

        const invalid = calculateValidationErrors(state);

        if (invalid) {
            const { errorMessage, validationErrors } = invalid;
            dispatch({ type: ActionTypes.SET_VALIDATION_ERRORS, companyId, validationErrors });
            return { errorMessage };
        }

        const createPayload = buildExchangePayload(state);

        const results = await createTokenUsingExchange(createPayload);

        const createdTokenCount = results.exchanges?.length ?? 0;
        return { createdTokenCount };
    };
